/**
 * DataLayer Event Tracking
 * Tracks user interactions for GTM/GA4
 */

window.dataLayer = window.dataLayer || [];

/**
 * Push event to dataLayer
 */
function pushEvent(eventName, eventData = {}) {
    window.dataLayer.push({
        event: eventName,
        ...eventData
    });
}

/**
 * Track mailto clicks
 */
function trackMailtoClicks() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="mailto:"]');
        if (link) {
            const email = link.href.replace('mailto:', '').split('?')[0];
            pushEvent('mailto_click', {
                email_address: email,
                click_location: getClickLocation(link)
            });
        }
    });
}

/**
 * Track tel clicks (click-to-call)
 */
function trackTelClicks() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="tel:"]');
        if (link) {
            const phone = link.href.replace('tel:', '');
            pushEvent('tel_click', {
                phone_number: phone,
                click_location: getClickLocation(link)
            });
        }
    });
}

/**
 * Track file downloads
 */
function trackDownloads() {
    const downloadExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', 'csv'];

    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (link && link.href) {
            const url = link.href.toLowerCase();
            const extension = url.split('.').pop().split('?')[0];

            if (downloadExtensions.includes(extension)) {
                const filename = url.split('/').pop().split('?')[0];
                pushEvent('file_download', {
                    file_name: filename,
                    file_type: extension,
                    file_url: link.href
                });
            }
        }
    });
}

/**
 * Track Formie form submissions
 */
function trackFormSubmissions() {
    // Listen for Formie's native submit success event
    document.addEventListener('onFormieSubmitSuccess', (e) => {
        const form = e.detail.form;
        const formHandle = form?.dataset?.formieFormHandle || 'unknown';
        const formId = form?.dataset?.formieFormId || 'unknown';

        pushEvent('form_submit', {
            form_name: formHandle,
            form_id: formId,
            form_location: getFormLocation()
        });
    });

    // Fallback: listen for native form submissions on Formie forms
    document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-fui-form]');
        if (form) {
            const formHandle = form.dataset.formieFormHandle || 'unknown';
            const formId = form.dataset.formieFormId || 'unknown';

            pushEvent('form_submit_attempt', {
                form_name: formHandle,
                form_id: formId,
                form_location: getFormLocation()
            });
        }
    });
}

/**
 * Track social link clicks
 */
function trackSocialClicks() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href*="linkedin.com"], a[href*="facebook.com"], a[href*="twitter.com"], a[href*="instagram.com"], a[href*="youtube.com"]');
        if (link) {
            const platform = getSocialPlatform(link.href);
            pushEvent('social_click', {
                social_platform: platform,
                social_url: link.href
            });
        }
    });
}

/**
 * Push consent status to dataLayer
 */
function trackConsentStatus() {
    // Check for common consent management platforms
    const consentData = {
        analytics_consent: getConsentStatus('analytics'),
        marketing_consent: getConsentStatus('marketing')
    };

    pushEvent('consent_status', consentData);

    // Listen for consent changes (CookieBot, OneTrust, etc.)
    window.addEventListener('CookiebotOnAccept', () => {
        updateConsentStatus();
    });

    window.addEventListener('CookiebotOnDecline', () => {
        updateConsentStatus();
    });
}

/**
 * Get consent status from common CMPs
 */
function getConsentStatus(type) {
    // CookieBot
    if (typeof Cookiebot !== 'undefined' && Cookiebot.consent) {
        if (type === 'analytics') return Cookiebot.consent.statistics;
        if (type === 'marketing') return Cookiebot.consent.marketing;
    }

    // OneTrust
    if (typeof OnetrustActiveGroups !== 'undefined') {
        if (type === 'analytics') return OnetrustActiveGroups.includes('C0002');
        if (type === 'marketing') return OnetrustActiveGroups.includes('C0004');
    }

    return null; // Unknown consent status
}

/**
 * Update consent status in dataLayer
 */
function updateConsentStatus() {
    pushEvent('consent_update', {
        analytics_consent: getConsentStatus('analytics'),
        marketing_consent: getConsentStatus('marketing')
    });
}

/**
 * Helper: Get click location context
 */
function getClickLocation(element) {
    if (element.closest('header')) return 'header';
    if (element.closest('footer')) return 'footer';
    if (element.closest('.modal')) return 'modal';
    if (element.closest('form')) return 'form';
    return 'content';
}

/**
 * Helper: Get form location based on page
 */
function getFormLocation() {
    const path = window.location.pathname;
    if (path.includes('contact')) return 'contact_page';
    if (path.includes('career') || path.includes('job')) return 'career_page';
    return 'other';
}

/**
 * Helper: Get social platform from URL
 */
function getSocialPlatform(url) {
    if (url.includes('linkedin')) return 'linkedin';
    if (url.includes('facebook')) return 'facebook';
    if (url.includes('twitter') || url.includes('x.com')) return 'twitter';
    if (url.includes('instagram')) return 'instagram';
    if (url.includes('youtube')) return 'youtube';
    return 'other';
}

/**
 * Initialize all tracking
 */
function initDataLayerTracking() {
    trackMailtoClicks();
    trackTelClicks();
    trackDownloads();
    trackFormSubmissions();
    trackSocialClicks();
    trackConsentStatus();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDataLayerTracking);
} else {
    initDataLayerTracking();
}
